// The Revenue Budget Review as an Excel workbook, for people to download
// and read the detail behind the Overview section (Seth 11/2: "add export
// and format it nicely so people can download and review the details").
//
//   Review   — the section's table with the detail opened out: every class
//              with its cost types under it (grouped, +/- in the margin),
//              Committed and ETC on both sides, the deltas and percentages
//              as formulas over the values beside them, the bill-to-cost
//              rates, the margin, and how each row's cost to go is priced.
//   Notes    — what each column is and where it comes from.
//
// Every figure is the Cost Plus Forecast's (helpers/costPlusForecast), laid
// out here; nothing is re-derived.
import moment from 'moment';
import { XLSX_STYLE, costReportBuffer } from './billingCostReportXlsx';
import { howText } from '../helpers/costPlusForecast';
import { reviewRows, statusOf, subLabel, TOL } from '../helpers/revenueReview';

const { NAVY, NAVY_LIGHT, WASH, WASH_SOFT, HAIR, INK, MUTED, BILL_INK, setBorder, fill } = XLSX_STYLE;
const LOSS = 'FFD13138';
const CAUTION = 'FF854D0E';
const CAUTION_WASH = 'FFFDF4DC';
const GOOD_WASH = 'FFE9F6EE';
const LOSS_WASH = 'FFFBEAEA';
const MONEY0 = '_("$"* #,##0_);_("$"* (#,##0);_("$"* "-"_);_(@_)';
const PCT = '0%;-0%;"-"';
const PCT1 = '+0.0%;-0.0%;"-"';
const RATE = '0.00"×";-0.00"×";"-"';

const colL = (n) => {
    let s = '';
    for (let x = n; x > 0; x = Math.floor((x - 1) / 26)) s = String.fromCharCode(65 + ((x - 1) % 26)) + s;
    return s;
};

// The Review sheet's columns, in order. `group` names the band over them.
export const COLS = [
    { k: 'label', h: 'Class / Cost Type', w: 34 },
    { k: 'status', h: 'Status', w: 26 },
    { k: 'cBudget', h: 'Budget', w: 15, fmt: MONEY0, group: 'Cost' },
    { k: 'cJtd', h: 'JTD', w: 15, fmt: MONEY0, group: 'Cost' },
    { k: 'cPct', h: '% Spent', w: 9, fmt: PCT, group: 'Cost' },
    { k: 'cCommit', h: 'Committed', w: 14, fmt: MONEY0, group: 'Cost' },
    { k: 'cEtc', h: 'ETC', w: 15, fmt: MONEY0, group: 'Cost' },
    { k: 'cEac', h: 'EAC', w: 15, fmt: MONEY0, group: 'Cost' },
    { k: 'cDelta', h: 'Δ Budget', w: 14, fmt: MONEY0, group: 'Cost' },
    { k: 'cDeltaPct', h: 'Δ %', w: 8, fmt: PCT1, group: 'Cost' },
    { k: 'bBudget', h: 'Budget', w: 15, fmt: MONEY0, group: 'Billable' },
    { k: 'bJtd', h: 'JTD', w: 15, fmt: MONEY0, group: 'Billable' },
    { k: 'bPct', h: '% Earned', w: 9, fmt: PCT, group: 'Billable' },
    { k: 'bCommit', h: 'Committed', w: 14, fmt: MONEY0, group: 'Billable' },
    { k: 'bEtc', h: 'ETC', w: 15, fmt: MONEY0, group: 'Billable' },
    { k: 'bEac', h: 'EAC', w: 15, fmt: MONEY0, group: 'Billable' },
    { k: 'bDelta', h: 'Δ Budget', w: 14, fmt: MONEY0, group: 'Billable' },
    { k: 'bDeltaPct', h: 'Δ %', w: 8, fmt: PCT1, group: 'Billable' },
    { k: 'rBid', h: 'Bid', w: 8, fmt: RATE, group: 'Bills at' },
    { k: 'rToDate', h: 'To date', w: 8, fmt: RATE, group: 'Bills at' },
    { k: 'rToGo', h: 'To go', w: 8, fmt: RATE, group: 'Bills at' },
    { k: 'margin', h: 'Margin EAC', w: 14, fmt: MONEY0, group: 'Margin' },
    { k: 'marginPct', h: 'Margin %', w: 9, fmt: PCT1, group: 'Margin' },
    { k: 'how', h: 'How the cost to go is priced', w: 70 },
];
const C = Object.fromEntries(COLS.map((c, i) => [c.k, colL(i + 1)]));
const LAST = COLS.length;

const NOTES = [
    ['Budget (cost)', 'The Day Zero cost budget plus approved change order cost, less the not-billable cost to date that sits on the Non-Billable row.'],
    ['JTD (cost)', "JDE cost to date on the class's cost types, every line, not-billable cost taken out to its own row."],
    ['Committed / ETC (cost)', "Open purchase orders by cost type, and the forecast's cost to go after them (the burn tool's hours, the Forecast tab's ETC rows)."],
    ['EAC (cost)', 'JTD + Committed + ETC. Δ Budget is EAC less Budget; Δ % is that over the budget.'],
    ['Budget (billable)', "The Revenue Budget: the Day Zero's Billable Cost column by cost code × cost type, plus approved CO cost at the bid's bill-to-cost."],
    ['JTD (billable)', "The Billing tab's priced lines to date: what has gone out on an invoice plus the rest priced at the rate sheet and contract rules, including the estimate for labor the sheet can't price yet."],
    ['Committed / ETC (billable)', 'The open POs and the cost to go priced the way the Billing tab will price them when they post (the "How the cost to go is priced" column says how).'],
    ['EAC (billable)', 'JTD + Committed + ETC on the billable side. Δ Budget is EAC less the Revenue Budget.'],
    ['Bills at', 'Bid = Revenue Budget ÷ cost budget. To date = billable to date ÷ cost to date. To go = billable to go ÷ cost to go.'],
    ['Margin', 'Billable EAC less cost EAC, and that as a share of the billable EAC.'],
    ['Status', `Within ±${Math.round(TOL * 100)}% of budget reads "on". "Billing short" is a billable EAC more than that under its budget; "billing ahead" more than that over. "On cost, billing short" is the one to look for: spending to plan while the billing runs under it.`],
    ['Non-Billable', "Cost the contract keeps off the invoice (fuel, truck pay, safety, lines a sent summary closed out at $0). It carries its own budget so a class on budget reads on budget; nothing to go, since the plan can't say what won't bill."],
    ['Source', 'The same model as the Cost Plus Forecast tab (helpers/costPlusForecast). Open the Cost Plus Forecast tab for the cards behind these rows.'],
];

/**
 * Build the workbook. `meta`: { job, jobName, versionDate, userName }.
 * `state`: { ready, planReady } — the billable side blank until the
 * Billing lines are priced, the EACs until the plan is in.
 */
export const buildRevenueReviewWorkbook = async (model, state = {}, meta = {}) => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Fusion';
    wb.created = new Date();
    wb.calcProperties = { fullCalcOnLoad: true };

    const { rows, total } = reviewRows(model, state);
    const ws = wb.addWorksheet('Review', { views: [{ showGridLines: false }] });
    ws.columns = COLS.map((c) => ({ width: c.w }));
    ws.properties.outlineProperties = { summaryBelow: false };

    // ── masthead ──────────────────────────────────────────────────────
    const title = ws.addRow([[meta.job, meta.jobName].filter(Boolean).join(' · ') || 'Revenue Budget Review']);
    title.height = 26;
    title.getCell(1).font = { bold: true, size: 18, color: { argb: NAVY } };
    ws.mergeCells(title.number, 1, title.number, LAST);
    const sub = ws.addRow(['Revenue Budget Review — cost against its budget, billable against the Revenue Budget, by LEMSO class']);
    sub.getCell(1).font = { size: 11, color: { argb: INK } };
    ws.mergeCells(sub.number, 1, sub.number, LAST);
    const prov = ws.addRow([[
        meta.versionDate ? `Forecast version ${moment(meta.versionDate).format('MMM D, YYYY')}` : null,
        `Prepared ${moment().format('MMMM D, YYYY')}`, meta.userName ? `by ${meta.userName}` : null,
        !state.ready ? 'Billable side not yet priced when exported' : null, !state.planReady ? 'Forecast not loaded when exported' : null,
    ].filter(Boolean).join('   ·   ')]);
    prov.getCell(1).font = { size: 10, color: { argb: MUTED } };
    ws.mergeCells(prov.number, 1, prov.number, LAST);
    const short = rows.filter((r) => r.kind === 'class' && statusOf(r).word === 'On cost, billing short');
    const chip = ws.addRow([short.length
        ? `${short.length} ${short.length === 1 ? 'class is' : 'classes are'} on cost but billing short: ${short.map((r) => r.label).join(', ')}`
        : 'No class is on cost and billing short']);
    chip.getCell(1).font = { bold: true, size: 10, color: { argb: short.length ? CAUTION : BILL_INK } };
    fill(chip.getCell(1), short.length ? CAUTION_WASH : GOOD_WASH);
    ws.mergeCells(chip.number, 1, chip.number, LAST);
    ws.addRow([]);

    // ── header: the bands, then the columns ───────────────────────────
    const band = ws.addRow(COLS.map((c) => c.group || ''));
    band.height = 18;
    let from = null;
    COLS.forEach((c, i) => {
        const cell = band.getCell(i + 1);
        fill(cell, NAVY_LIGHT);
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        const next = COLS[i + 1];
        if (c.group && from == null) from = i + 1;
        if (c.group && (!next || next.group !== c.group)) {
            if (from != null && from < i + 1) ws.mergeCells(band.number, from, band.number, i + 1);
            from = null;
        }
    });
    const head = ws.addRow(COLS.map((c) => c.h));
    head.height = 30;
    head.eachCell((cell, i) => {
        fill(cell, NAVY);
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: i <= 2 || i === LAST ? 'left' : 'right', vertical: 'middle', wrapText: true };
    });
    ws.views = [{ state: 'frozen', ySplit: head.number, xSplit: 2, showGridLines: false }];

    // ── rows ──────────────────────────────────────────────────────────
    const r2 = (n) => (n == null ? null : Math.round(n * 100) / 100);
    const F = (f) => ({ formula: f });
    const writeRow = (r, { level = 0, label, bold = false, wash = null, nb = false, isTotal = false } = {}) => {
        const s = statusOf(r);
        const bill = nb ? null : r.jtdBillShown;
        const row = ws.addRow([
            label, s.word,
            nb ? null : r2(r.curCostBudget), r2(r.jtdCost), null, nb ? null : r2(r.committed), nb ? null : r2(r.etc), r2(r.costEacShown), null, null,
            nb ? null : r2(r.curBillBudget), r2(bill), null, nb || !state.ready ? null : r2(r.billCommitted), nb || !state.ready ? null : r2(r.billEtc), nb ? null : r2(r.billEacShown), null, null,
            null, null, null, null, null,
            nb ? '' : howText(r.toGoHow),
        ]);
        const n = row.number;
        if (!nb) {
            row.getCell(C.cPct).value = F(`IFERROR(${C.cJtd}${n}/${C.cBudget}${n},"")`);
            row.getCell(C.cDelta).value = r.costEacShown == null ? null : F(`${C.cEac}${n}-${C.cBudget}${n}`);
            row.getCell(C.cDeltaPct).value = r.costEacShown == null ? null : F(`IFERROR(${C.cDelta}${n}/${C.cBudget}${n},"")`);
            row.getCell(C.bPct).value = bill == null ? null : F(`IFERROR(${C.bJtd}${n}/${C.bBudget}${n},"")`);
            row.getCell(C.bDelta).value = r.billEacShown == null ? null : F(`${C.bEac}${n}-${C.bBudget}${n}`);
            row.getCell(C.bDeltaPct).value = r.billEacShown == null ? null : F(`IFERROR(${C.bDelta}${n}/${C.bBudget}${n},"")`);
            row.getCell(C.rBid).value = F(`IFERROR(${C.bBudget}${n}/${C.cBudget}${n},"")`);
            row.getCell(C.rToDate).value = bill == null ? null : F(`IFERROR(${C.bJtd}${n}/${C.cJtd}${n},"")`);
            row.getCell(C.rToGo).value = r.billEacShown == null ? null : F(`IFERROR((${C.bCommit}${n}+${C.bEtc}${n})/(${C.cCommit}${n}+${C.cEtc}${n}),"")`);
            row.getCell(C.margin).value = r.billEacShown == null ? null : F(`${C.bEac}${n}-${C.cEac}${n}`);
            row.getCell(C.marginPct).value = r.billEacShown == null ? null : F(`IFERROR(${C.margin}${n}/${C.bEac}${n},"")`);
        }
        row.outlineLevel = level;
        row.eachCell({ includeEmpty: true }, (cell, i) => {
            const col = COLS[i - 1];
            if (col.fmt) cell.numFmt = col.fmt;
            cell.font = { size: 10, bold, color: { argb: level ? MUTED : INK } };
            cell.alignment = { vertical: 'top', horizontal: i <= 2 || i === LAST ? 'left' : 'right', wrapText: i === LAST, indent: i === 1 ? level : 0 };
            if (wash) fill(cell, wash);
            setBorder(cell, isTotal ? ['top', 'bottom'] : ['bottom'], isTotal ? NAVY : HAIR);
        });
        // The status in its tone; the deltas red where they hurt, green where they help.
        const st = row.getCell(C.status);
        if (s.tone === 'caution') { st.font = { ...st.font, color: { argb: CAUTION } }; fill(st, CAUTION_WASH); }
        else if (s.tone === 'bad') { st.font = { ...st.font, color: { argb: LOSS } }; fill(st, LOSS_WASH); }
        else if (s.tone === 'good') { st.font = { ...st.font, color: { argb: BILL_INK } }; fill(st, GOOD_WASH); }
        else st.font = { ...st.font, color: { argb: MUTED } };
        if (!nb && r.costEacShown != null) {
            const d = r.costEacShown - r.curCostBudget;
            if (Math.abs(d) >= 0.5) [C.cDelta, C.cDeltaPct].forEach((k) => { row.getCell(k).font = { ...row.getCell(k).font, color: { argb: d > 0 ? LOSS : BILL_INK } }; });
        }
        if (!nb && r.billEacShown != null) {
            const d = r.billEacShown - r.curBillBudget;
            if (Math.abs(d) >= 0.5) [C.bDelta, C.bDeltaPct].forEach((k) => { row.getCell(k).font = { ...row.getCell(k).font, color: { argb: d < 0 ? LOSS : BILL_INK } }; });
            const m = r.billEacShown - r.costEacShown;
            if (m < 0) [C.margin, C.marginPct].forEach((k) => { row.getCell(k).font = { ...row.getCell(k).font, color: { argb: LOSS } }; });
        }
        return row;
    };

    rows.forEach((r) => {
        writeRow(r, { label: r.label, bold: true, wash: WASH_SOFT, nb: r.kind === 'nb' });
        r.subs.forEach((s) => writeRow(s, { level: 1, label: subLabel(s) }));
    });
    writeRow(total, { label: 'Total Costs', bold: true, wash: WASH, isTotal: true });

    // ── notes ─────────────────────────────────────────────────────────
    ws.addRow([]);
    const nh = ws.addRow(['Notes']);
    nh.getCell(1).font = { bold: true, size: 11, color: { argb: NAVY } };
    NOTES.forEach(([what, text]) => {
        const row = ws.addRow([what, text]);
        row.getCell(1).font = { bold: true, size: 10, color: { argb: INK } };
        row.getCell(1).alignment = { vertical: 'top' };
        row.getCell(2).font = { size: 10, color: { argb: INK } };
        row.getCell(2).alignment = { vertical: 'top', wrapText: true };
        ws.mergeCells(row.number, 2, row.number, Math.min(LAST, 12));
        row.height = Math.max(15, Math.ceil(text.length / 120) * 15);
    });

    ws.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, printTitlesRow: `${band.number}:${head.number}` };
    return wb;
};

/** Build and hand the file to the browser. Returns its name. */
export const downloadRevenueReview = async (model, state = {}, meta = {}) => {
    const wb = await buildRevenueReviewWorkbook(model, state, meta);
    const buffer = await costReportBuffer(wb);
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const name = `Revenue_Budget_Review_${meta.job || 'job'}_${meta.versionDate ? moment(meta.versionDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')}.xlsx`;
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    return name;
};
